import axiosConfig from "./axiosConfig";

// Système de cache pour les requêtes GET
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes en millisecondes

class RubriqueService {
  constructor() {
    this.baseUrl = "/v1/rubriques";
  }

  /**
   * Vérifie si des données sont disponibles dans le cache
   * @param {string} cacheKey Clé de cache
   * @param {boolean} useCache Utiliser le cache ou non
   * @returns {Object|null} Données en cache ou null
   */
  _checkCache(cacheKey, useCache = true) {
    if (useCache && cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      if (Date.now() - cachedData.timestamp < CACHE_DURATION) {
        return cachedData.data;
      }
    }
    return null;
  }

  /**
   * Met en cache des données
   * @param {string} cacheKey Clé de cache
   * @param {Object} data Données à stocker
   * @param {boolean} useCache Activer la mise en cache
   */
  _setCache(cacheKey, data, useCache = true) {
    if (useCache) {
      cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });
    }
  }

  /**
   * Récupère toutes les rubriques
   * @param {boolean} useCache Utiliser le cache si disponible
   * @returns {Promise<Array>} Liste des rubriques
   */
  getAll(useCache = true) {
    const cacheKey = 'rubriques-all';
    const cachedData = this._checkCache(cacheKey, useCache);
    
    if (cachedData) return Promise.resolve(cachedData);
    
    return axiosConfig.get(this.baseUrl)
      .then(response => {
        const data = response.data.data || [];
        this._setCache(cacheKey, data, useCache);
        return data;
      })
      .catch(error => Promise.reject(error.response?.data || error.message));
  }

  /**
   * Crée une nouvelle rubrique
   * @param {FormData} formData Données du formulaire
   * @returns {Promise<Object>} La rubrique créée
   */
  createRubrique(formData) {
    return axiosConfig.post(this.baseUrl, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Accept: "application/json",
      },
    })
    .then(response => {
      this.invalidateCache();
      return response.data;
    })
    .catch(error => {
      console.error("❌ Erreur lors de la création:", error.response?.data || error.message);
      return Promise.reject(error.response?.data || { message: error.message });
    });
  }

  /**
   * Met à jour une rubrique existante
   * @param {number} id ID de la rubrique
   * @param {FormData} formData Nouvelles données
   * @returns {Promise<Object>} La rubrique mise à jour
   */
  updateRubrique(id, formData) {
    // S'assurer que _method est défini
    if (formData instanceof FormData && !formData.has("_method")) {
      formData.append("_method", "PUT");
    }

    return axiosConfig.post(`${this.baseUrl}/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Accept: "application/json",
      },
    })
    .then(response => {
      this.invalidateCache();
      return response.data;
    })
    .catch(error => {
      console.error("❌ Erreur lors de la mise à jour:", error.response?.data || error.message);
      return Promise.reject(error.response?.data || { message: error.message });
    });
  }

  /**
   * Supprime une rubrique
   * @param {number} id ID de la rubrique
   * @returns {Promise<Object>} Résultat de la suppression
   */
  deleteRubrique(id) {
    // Vérifier d'abord si la rubrique existe
    return this.getRubriqueById(id)
      .then(() => axiosConfig.delete(`${this.baseUrl}/${id}`))
      .then(response => {
        this.invalidateCache();
        return response.data;
      })
      .catch(error => Promise.reject(error.response?.data || error.message));
  }

  /**
   * Récupère une rubrique par son ID
   * @param {number} id ID de la rubrique
   * @param {boolean} useCache Utiliser le cache si disponible
   * @returns {Promise<Object>} La rubrique
   */
  getRubriqueById(id, useCache = true) {
    const cacheKey = `rubrique-${id}`;
    const cachedData = this._checkCache(cacheKey, useCache);
    
    if (cachedData) return Promise.resolve(cachedData);
    
    return axiosConfig.get(`${this.baseUrl}/${id}`)
      .then(response => {
        this._setCache(cacheKey, response.data, useCache);
        return response.data;
      })
      .catch(error => Promise.reject(error.response?.data || error.message));
  }

  /**
   * Crée plusieurs rubriques en parallèle
   * @param {Array<FormData>} formDataArray Tableau des données des rubriques
   * @returns {Promise<Array>} Résultats des créations
   */
  createMultipleRubriques(formDataArray) {
    const promises = formDataArray.map(formData => this.createRubrique(formData));
    return Promise.all(promises);
  }

  /**
   * Met à jour plusieurs rubriques en parallèle
   * @param {Array<Object>} updateDataArray Tableau d'objets {id, formData}
   * @returns {Promise<Array>} Résultats des mises à jour
   */
  updateMultipleRubriques(updateDataArray) {
    const promises = updateDataArray.map(({ id, formData }) => 
      this.updateRubrique(id, formData)
    );
    return Promise.all(promises);
  }

  /**
   * Invalide tout le cache des rubriques
   */
  invalidateCache() {
    for (const key of cache.keys()) {
      if (key.startsWith('rubrique')) {
        cache.delete(key);
      }
    }
  }
}

const rubriqueServiceInstance = new RubriqueService();
export default rubriqueServiceInstance;